'use client';

import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { PopularProducts } from '@/app/[abTest]/[locale]/(spreadshirt)/shop/design/actions/popularProducts';
import type { SimilarProductItemsResponse } from '@/app/[abTest]/[locale]/(spreadshirt)/shop/design/actions/similarProductItems';
import { SimilarProductsRecommendations } from '@/app/[abTest]/[locale]/(spreadshirt)/shop/design/components/SimilarProductsRecommendations';
import type { DesignProductFilter } from '@/app/[abTest]/[locale]/(spreadshirt)/shop/design/utility/recommendations';
import { Locale } from '@/utility/locale';
import { get, toQueryString } from '@/utility/requestHelpers/clientRequestHelpers';
type DesignSimilarProductsWrapperProps = {
  shopId: string;
  locale: Locale;
  compositionId: Composition['compositionId'];
  compositionName: string;
  currentSellableId: Sellable['sellableId'];
  popularProducts: PopularProducts;
  otherFilters: DesignProductFilter[];
};
export const SimilarProductsRecommendationsWrapper = t0 => {
  const $ = _c(43);
  const {
    shopId,
    locale,
    compositionId,
    compositionName,
    currentSellableId,
    popularProducts,
    otherFilters
  } = t0;
  let t1;
  if ($[0] !== popularProducts.filter) {
    t1 = popularProducts.filter ? [popularProducts.filter] : [];
    $[0] = popularProducts.filter;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== otherFilters || $[3] !== t1) {
    t2 = [...t1, ...otherFilters];
    $[2] = otherFilters;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const designProductFilters = t2;
  const [activeFilter, setActiveFilter] = useState(designProductFilters[0]);
  const isPopularActive = activeFilter === popularProducts.filter;
  const t3 = activeFilter?.id;
  let t4;
  if ($[5] !== compositionId || $[6] !== currentSellableId || $[7] !== locale || $[8] !== shopId || $[9] !== t3) {
    t4 = ["similarProductItems", shopId, locale, compositionId, currentSellableId, t3];
    $[5] = compositionId;
    $[6] = currentSellableId;
    $[7] = locale;
    $[8] = shopId;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== activeFilter?.productTypeIds || $[12] !== compositionId || $[13] !== currentSellableId || $[14] !== locale || $[15] !== shopId) {
    t5 = async () => get(`/internal/api/similarProductItems?${toQueryString({
      shopId,
      locale,
      compositionId,
      currentSellableId,
      productTypeIds: activeFilter?.productTypeIds.join(",")
    })}`);
    $[11] = activeFilter?.productTypeIds;
    $[12] = compositionId;
    $[13] = currentSellableId;
    $[14] = locale;
    $[15] = shopId;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const t6 = !!activeFilter && !isPopularActive;
  let t7;
  if ($[17] !== popularProducts.items) {
    t7 = previousData => previousData ?? {
      mappedDesignProductItems: popularProducts.items
    };
    $[17] = popularProducts.items;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t4 || $[20] !== t5 || $[21] !== t6 || $[22] !== t7) {
    t8 = {
      queryKey: t4,
      queryFn: t5,
      enabled: t6,
      staleTime: 3600000,
      placeholderData: t7
    };
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const {
    data: itemsData,
    isFetching: isFetchingItems
  } = useQuery(t8);
  const [showSkeleton, setShowSkeleton] = useState(false);
  let t9;
  if ($[24] !== isFetchingItems || $[25] !== isPopularActive) {
    t9 = () => {
      if (!isFetchingItems || isPopularActive) {
        setShowSkeleton(false);
        return;
      }
      const timeoutId = setTimeout(() => setShowSkeleton(true), 1000);
      return () => clearTimeout(timeoutId);
    };
    $[24] = isFetchingItems;
    $[25] = isPopularActive;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const t10 = activeFilter?.id;
  let t11;
  if ($[27] !== isFetchingItems || $[28] !== isPopularActive || $[29] !== t10) {
    t11 = [isFetchingItems, isPopularActive, t10];
    $[27] = isFetchingItems;
    $[28] = isPopularActive;
    $[29] = t10;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  useEffect(t9, t11);
  const isLoadingItems = !isPopularActive && showSkeleton;
  let t12;
  if ($[31] !== isPopularActive || $[32] !== itemsData?.mappedDesignProductItems || $[33] !== popularProducts.items) {
    t12 = isPopularActive ? popularProducts.items : itemsData?.mappedDesignProductItems ?? [];
    $[31] = isPopularActive;
    $[32] = itemsData?.mappedDesignProductItems;
    $[33] = popularProducts.items;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  const activeItems = t12;
  if (!designProductFilters.length) {
    return null;
  }
  const t13 = Number(shopId);
  let t14;
  if ($[35] !== activeFilter || $[36] !== activeItems || $[37] !== compositionId || $[38] !== compositionName || $[39] !== designProductFilters || $[40] !== isLoadingItems || $[41] !== t13) {
    t14 = <SimilarProductsRecommendations compositionId={compositionId} compositionName={compositionName} designProductFilters={designProductFilters} activeFilter={activeFilter} onActiveFilterChange={setActiveFilter} mappedDesignProductItems={activeItems} isLoadingItems={isLoadingItems} shopId={t13} />;
    $[35] = activeFilter;
    $[36] = activeItems;
    $[37] = compositionId;
    $[38] = compositionName;
    $[39] = designProductFilters;
    $[40] = isLoadingItems;
    $[41] = t13;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  return t14;
};